// CSV parsing + product-config row mapping for the native Bulk Import page.
// The column set round-trips with GET /api/products-export (routes/
// product-admin.js EXPORT_COLUMNS) so export → edit in a spreadsheet →
// re-import works without translation.
import type { ProductConfig } from './product-config';
import { ALL_SIZES } from './product-config';

// RFC-4180-ish parser: quoted fields, escaped quotes, CRLF/LF.
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      rows.push(row); row = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop fully-empty trailing rows.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

function num(v: string | undefined): number | undefined {
  if (v == null || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function str(v: string | undefined): string | undefined {
  const t = (v ?? '').trim();
  return t === '' ? undefined : t;
}

export type CsvRowResult = { index: number; product?: ProductConfig; error?: string };

// Maps export-format rows into product docs. Unknown columns are ignored;
// required: 'Product ID' + 'Style Number'.
export function rowsToProducts(header: string[], rows: string[][]): CsvRowResult[] {
  const col = new Map(header.map((h, i) => [h.trim().toLowerCase(), i]));
  const cell = (row: string[], name: string) => {
    const idx = col.get(name.toLowerCase());
    return idx == null ? undefined : row[idx];
  };
  if (!col.has('product id')) {
    return [{ index: 0, error: "Missing required column 'Product ID' — export from Products for the right format." }];
  }

  return rows.map((row, index) => {
    const id = str(cell(row, 'Product ID'));
    if (!id) return { index, error: 'Missing Product ID' };
    const styleNumber = str(cell(row, 'Style Number'));
    if (!styleNumber) return { index, error: 'Missing Style Number' };

    const sizes = ALL_SIZES.filter((s) => /^(yes|y|true|x|1)$/i.test((cell(row, s) ?? '').trim()));
    const tags = (cell(row, 'Tags') ?? '').split(/[;,]/).map((t) => t.trim()).filter(Boolean);

    const product: ProductConfig = {
      id,
      productTitle: str(cell(row, 'Product Title')),
      displayName: str(cell(row, 'Display Name')),
      shortName: str(cell(row, 'Short Name')),
      category: str(cell(row, 'Category')),
      description: str(cell(row, 'Description')),
      tier: str(cell(row, 'Tier')),
      tierLabel: str(cell(row, 'Tier Label')),
      brandPartner: str(cell(row, 'Brand Partner')),
      gender: str(cell(row, 'Gender')),
      sortOrder: num(cell(row, 'Sort Order')),
      active: !/^no|false|0$/i.test((cell(row, 'Active') ?? 'yes').trim()),
      baseProduct: {
        styleNumber,
        color: str(cell(row, 'Color')),
        colorCode: str(cell(row, 'Color Code')),
        supplier: str(cell(row, 'Supplier Brand')),
        fabricComposition: str(cell(row, 'Fabric Composition')),
      },
      pricing: {
        basePrice: num(cell(row, 'Base Price')),
        compareAtPrice: num(cell(row, 'Compare At Price')),
        cost: num(cell(row, 'Cost')),
        currency: str(cell(row, 'Currency')) || 'USD',
      },
      printArea: {
        position: str(cell(row, 'Position')),
        positionDisplay: str(cell(row, 'Position Display')),
        width: num(cell(row, 'Width (inches)')),
        height: num(cell(row, 'Height (inches)')),
      },
      decoration: {
        method: str(cell(row, 'Method')),
        leadTime: num(cell(row, 'Lead Time (days)')),
        detail: str(cell(row, 'Decoration Detail')),
      },
      sizes: sizes.length ? [...sizes] : undefined,
      mockup: {
        templateFile: str(cell(row, 'Template File')),
        logoX: num(cell(row, 'Logo Position X')),
        logoY: num(cell(row, 'Logo Position Y')),
        logoMaxWidth: num(cell(row, 'Logo Max Width (px)')),
        logoMaxHeight: num(cell(row, 'Logo Max Height (px)')),
      },
      shopify: {
        productType: str(cell(row, 'Product Type')),
        vendor: str(cell(row, 'Vendor')),
        tags: tags.length ? tags : undefined,
        sizeChartUrl: str(cell(row, 'Size Chart URL')),
      },
    };
    return { index, product };
  });
}
